import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { fetchActivityWarnings } from "PFApp/booking/services/api";
import { MisalignmentItem } from "PFApp/booking/types";
import { useMemo } from "react";

import { activityKeys } from "./query_keys";

export type ActivityWarning = {
  misalignments: MisalignmentItem[];
  profileIds: number[];
  vacancyId: number;
};
export type ActivityWarningsIndex = Record<number, ActivityWarning[]>;
type ActivityIdWithRowKey = { rowKey?: string; id: number };

const groupLoadingByRowKey = (
  queries: UseQueryResult<ActivityWarningsIndex>[],
  activityRows: ActivityIdWithRowKey[]
): Record<string, boolean> =>
  queries.reduce((result, { isLoading, isFetching }, index) => {
    const { rowKey, id } = activityRows[index];
    return {
      ...result,
      [rowKey ?? id]: isLoading || isFetching
    };
  }, {});

export const useActivityWarnings = (activityRows: ActivityIdWithRowKey[]) => {
  const stringifiedActivityRows = JSON.stringify(activityRows);
  const queriesConfig = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      activityRows.map(({ id }) => ({
        queryKey: activityKeys.warnings(id),

        queryFn: async (): Promise<ActivityWarningsIndex> => ({
          [id]: await fetchActivityWarnings(id)
        })
      })),
    [stringifiedActivityRows]
  );
  const queries = useQueries({
    queries: queriesConfig
  });

  const isLoading = useMemo(
    () => !!queries.find(({ isLoading, isFetching }) => isLoading || isFetching),
    [queries]
  );

  const activityWarningsLoadingByRowKey = useMemo<Record<string, boolean>>(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => groupLoadingByRowKey(queries, activityRows),
    [isLoading, stringifiedActivityRows]
  );

  const warningsByActivity = useMemo<ActivityWarningsIndex>(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => Object.assign({}, ...queries.map(({ data }) => data)),
    [isLoading, stringifiedActivityRows]
  );

  return {
    warningsByActivity,
    activityWarningsLoadingByRowKey
  };
};
